/** @format */
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import React, {
    Fragment,
    FunctionComponent,
    ReactNode,
    useContext,
    useEffect,
} from 'react';

import { MarketContext } from '@toucan/client/global/market';
import { ProductState } from '@toucan/client/global/product';

import { ProductComparisonItemOutOfStockHeader } from '@toucan/client/features/model/components/model-overview/hooks/use-product-comparison/components/product-comparison-item/components/out-of-stock-header';
import { useProductSummary } from '@toucan/client/features/model/components/model-overview/hooks/use-product-comparison/components/product-comparison-item/hooks/use-product-summary';

import { ProductComparisonItemErrorHeader } from './components/error-header';
import { ProductComparisonItemInStock } from './components/in-stock-header';
import { DEFAULT_TEST_ID } from './constants';
import { useRowData } from './hooks';
import {
    ProductComparisonItemSection,
    ProductComparisonTableLabel,
} from './product-comparison-item.styles';
import {
    ProductComparisonItemContentSkeleton,
    ProductComparisonItemSkeleton,
} from './skeleton';
import { ProductComparisonItemProps } from './types';

export const ProductComparisonItem: FunctionComponent<
    ProductComparisonItemProps
> = ({
    columnIndex,
    productId,
    modelUrlSegment,
    rowCount,
    setRowCount,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
}) => {
    const { activeMarket } = useContext(MarketContext);
    const { data, isFetching, isError, refetch } = useProductSummary(
        productId,
        activeMarket.code
    );
    const rows = useRowData(data, rowCount, modelUrlSegment);
    const isOutOfStock = Boolean(data) && data?.state !== ProductState.ONLINE;
    function renderHeader() {
        if (isFetching) {
            return (
                <ProductComparisonItemSkeleton
                    data-testid={`${dataTestId}__skeleton`}
                    columnIndex={columnIndex}
                />
            );
        }

        if (isError || !data) {
            return (
                <ProductComparisonItemErrorHeader
                    data-testid={`${dataTestId}__error`}
                    columnIndex={columnIndex}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- Legacy code: do not use as an example.
                    onRefetch={refetch}
                />
            );
        }

        if (isOutOfStock) {
            return (
                <ProductComparisonItemOutOfStockHeader
                    data-testid={`${dataTestId}__out-of-stock`}
                    columnIndex={columnIndex}
                />
            );
        }

        return (
            <ProductComparisonItemInStock
                data-testid={dataTestId}
                columnIndex={columnIndex}
                modelUrlSegment={modelUrlSegment}
                productData={data}
            />
        );
    }

    function renderLabel(label: string | null) {
        return (
            label ?? (
                <Skeleton
                    data-testid={`${dataTestId}__skeleton-label`}
                    variant={SkeletonVariant.TEXT}
                    width="11rem"
                />
            )
        );
    }

    function renderContent(content: ReactNode | null, index: number) {
        if (isFetching) {
            return (
                <ProductComparisonItemContentSkeleton
                    rowIndex={index}
                    rowCount={rowCount}
                />
            );
        }

        if (isError || isOutOfStock) {
            return '-';
        }

        return content;
    }

    useEffect(() => {
        if (rowCount !== rows.length) {
            setRowCount(rows.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [rows.length]);

    return (
        <Fragment>
            {columnIndex === 0 && (
                <Fragment>
                    <ProductComparisonTableLabel />
                    {rows.map((row) => (
                        <ProductComparisonTableLabel key={row.id}>
                            {renderLabel(row.label)}
                        </ProductComparisonTableLabel>
                    ))}
                </Fragment>
            )}
            {renderHeader()}
            {rows.map((row, rowIndex) => (
                <ProductComparisonItemSection
                    $isInStock={!isError && !isOutOfStock}
                    $columnIndex={columnIndex}
                    $rowIndex={rowIndex}
                    // eslint-disable-next-line @mpb-com/mpb/transient-props-last -- Legacy code: do not use as an example.
                    key={row.id}
                    data-testid={`${dataTestId}__${row.id}`}
                >
                    {!isError && !isOutOfStock && (
                        <strong>{renderLabel(row.label)}</strong>
                    )}
                    {renderContent(row.value, rowIndex)}
                </ProductComparisonItemSection>
            ))}
        </Fragment>
    );
};
